import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import SEO, { organizationSchema, softwareSchema } from '../components/SEO';

/* ================================================================== */
/*  CSS KEYFRAMES                                                      */
/* ================================================================== */
const KEYFRAMES = `
@keyframes blink{0%,100%{opacity:1}50%{opacity:0}}
@keyframes fadeInUp{from{opacity:0;transform:translateY(40px)}to{opacity:1;transform:translateY(0)}}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
@keyframes slideInLeft{from{opacity:0;transform:translateX(-40px)}to{opacity:1;transform:translateX(0)}}
@keyframes slideInRight{from{opacity:0;transform:translateX(40px)}to{opacity:1;transform:translateX(0)}}
@keyframes bounceIn{0%{opacity:0;transform:scale(.3)}50%{opacity:1;transform:scale(1.05)}70%{transform:scale(.95)}100%{transform:scale(1)}}
@keyframes pulseGlow{0%,100%{box-shadow:0 0 20px rgba(124,58,237,.2)}50%{box-shadow:0 0 60px rgba(124,58,237,.5)}}
@keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}
@keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}
@keyframes typingDot{0%,60%,100%{transform:translateY(0)}30%{transform:translateY(-8px)}}
@keyframes shake{0%,100%{transform:rotate(0)}10%,30%,50%,70%,90%{transform:rotate(-3deg)}20%,40%,60%,80%{transform:rotate(3deg)}}
@keyframes conveyor{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
@keyframes confettiFall{0%{transform:translateY(-10px) rotate(0);opacity:1}100%{transform:translateY(150vh) rotate(720deg);opacity:0}}
@keyframes cardFlip{0%{transform:perspective(600px) rotateY(0)}100%{transform:perspective(600px) rotateY(180deg)}}
@keyframes glowRing{0%,100%{box-shadow:0 0 5px rgba(124,58,237,.3),0 0 20px rgba(124,58,237,.1)}50%{box-shadow:0 0 20px rgba(124,58,237,.6),0 0 60px rgba(124,58,237,.2)}}
@keyframes konamiRainbow{0%{filter:hue-rotate(0deg)}100%{filter:hue-rotate(360deg)}}
@keyframes checkDraw{from{stroke-dashoffset:24}to{stroke-dashoffset:0}}
@keyframes marquee{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
@keyframes meshMove1{0%,100%{transform:translate(0,0)}50%{transform:translate(80px,-60px)}}
@keyframes meshMove2{0%,100%{transform:translate(0,0)}50%{transform:translate(-60px,80px)}}
@keyframes meshMove3{0%,100%{transform:translate(0,0)}50%{transform:translate(40px,50px)}}
@keyframes flipDown{0%{transform:perspective(300px) rotateX(0);opacity:1}50%{transform:perspective(300px) rotateX(-90deg);opacity:0}51%{transform:perspective(300px) rotateX(90deg);opacity:0}100%{transform:perspective(300px) rotateX(0);opacity:1}}
@keyframes scaleReveal{from{transform:scale(0.8);opacity:0}to{transform:scale(1);opacity:1}}
@keyframes slideDown{from{transform:translateY(-100%);opacity:0}to{transform:translateY(0);opacity:1}}
@keyframes cardDeal{from{opacity:0;transform:translateY(60px) rotate(-3deg)}to{opacity:1;transform:translateY(0) rotate(0)}}
@keyframes sparkle{0%{opacity:1;transform:scale(1)}100%{opacity:0;transform:scale(0)}}
@keyframes circleWipe{0%{clip-path:circle(0% at 50% 50%)}100%{clip-path:circle(100% at 50% 50%)}}
@keyframes celebration{0%{transform:scale(0) rotate(-10deg);opacity:0}60%{transform:scale(1.1) rotate(2deg);opacity:1}100%{transform:scale(1) rotate(0);opacity:1}}
@keyframes confidenceFill{from{width:0}to{width:var(--conf)}}
@keyframes countUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
@keyframes spotsFlash{0%,100%{opacity:1}50%{opacity:0.6}}
`;

/* ================================================================== */
/*  SVG ICONS                                                          */
/* ================================================================== */
const IconStore = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
const IconPricing = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>;
const IconMenu = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-6 h-6"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>;
const IconX = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-6 h-6"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const IconSun = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-5 h-5"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>;
const IconMoon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-5 h-5"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>;
const IconArrowRight = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-4 h-4 inline ml-1"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>;
const IconCheck = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-4 h-4 text-green-400 inline mr-1 flex-shrink-0"><polyline points="20 6 9 17 4 12"/></svg>;
const IconSend = () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>;
const IconTwitter = () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>;
const IconLinkedIn = () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>;
const IconGitHub = () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg>;
const IconWhatsApp = () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>;
const IconRefresh = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-4 h-4"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>;
const IconZap = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-5 h-5"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>;
const IconTrendingUp = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-5 h-5"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>;
const IconInvoice = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-5 h-5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>;
const IconHeart = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-5 h-5"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>;
const IconPackage = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-5 h-5"><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>;
const IconMail = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-5 h-5"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>;
const IconStar = () => <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-yellow-400"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;
const IconGlobe = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-5 h-5"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>;
const IconTag = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-5 h-5"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>;

/* ================================================================== */
/*  HOOKS                                                              */
/* ================================================================== */
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

function useScrollY() {
  const [y, setY] = useState(0);
  useEffect(() => {
    const h = () => setY(window.scrollY);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);
  return y;
}

function useScrollPercent() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const h = () => {
      const doc = document.documentElement;
      const total = doc.scrollHeight - doc.clientHeight;
      setPct(total > 0 ? (window.scrollY / total) * 100 : 0);
    };
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);
  return pct;
}

/* ================================================================== */
/*  HELPER                                                             */
/* ================================================================== */
const SIGNUP = '/login?signup=true';
const cls = (...c: (string | false | undefined | null)[]) => c.filter(Boolean).join(' ');

/* ================================================================== */
/*  1. SCROLL PROGRESS BAR                                             */
/* ================================================================== */
const ScrollProgressBar = memo(() => {
  const pct = useScrollPercent();
  return (
    <div className="fixed top-0 left-0 w-full h-[3px] z-50">
      <div
        className="h-full bg-gradient-to-r from-violet-500 via-purple-500 to-indigo-500 transition-[width] duration-100"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
});

/* ================================================================== */
/*  2. STICKY NAVBAR                                                   */
/* ================================================================== */
const Navbar = memo(({ dark, toggleDark }: { dark: boolean; toggleDark: () => void }) => {
  const scrollY = useScrollY();
  const [menuOpen, setMenuOpen] = useState(false);
  const scrolled = scrollY > 40;

  return (
    <nav className={cls(
      'fixed top-0 left-0 w-full z-40 transition-all duration-300',
      scrolled
        ? (dark ? 'bg-gray-900/95 backdrop-blur-md shadow-lg shadow-black/20' : 'bg-white/95 backdrop-blur-md shadow-lg shadow-black/5')
        : 'bg-transparent'
    )}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <a href="/" className={cls('text-xl font-bold tracking-tight', dark ? 'text-white' : (scrolled ? 'text-gray-900' : 'text-white'))}>
          AI Commerce OS
        </a>
        <div className="hidden md:flex items-center gap-8">
          {[['Features', '#features'], ['Pricing', '#pricing'], ['About', '#about']].map(([l, h]) => (
            <a key={l} href={h} className={cls(
              'text-sm font-medium transition-colors',
              dark ? 'text-gray-300 hover:text-white' : (scrolled ? 'text-gray-600 hover:text-gray-900' : 'text-gray-300 hover:text-white')
            )}>{l}</a>
          ))}
          <button onClick={toggleDark} className={cls('p-2 rounded-lg transition-colors', dark ? 'text-gray-300 hover:text-white hover:bg-gray-800' : (scrolled ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100' : 'text-gray-300 hover:text-white hover:bg-white/10'))}>
            {dark ? <IconSun /> : <IconMoon />}
          </button>
          <a href="/login" className={cls('text-sm font-medium transition-colors', dark ? 'text-gray-300 hover:text-white' : (scrolled ? 'text-gray-600 hover:text-gray-900' : 'text-gray-300 hover:text-white'))}>Login</a>
          <a href={SIGNUP} className="px-5 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold rounded-full transition-all shadow-lg shadow-violet-600/25 hover:shadow-violet-600/40">
            Start Free
          </a>
        </div>
        <button className="md:hidden p-2" onClick={() => setMenuOpen(!menuOpen)}>
          <span className={dark ? 'text-white' : (scrolled ? 'text-gray-900' : 'text-white')}>{menuOpen ? <IconX /> : <IconMenu />}</span>
        </button>
      </div>
      {menuOpen && (
        <div className={cls('md:hidden border-t px-4 pb-4 pt-2 space-y-2', dark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100')}>
          {[['Features', '#features'], ['Pricing', '#pricing'], ['About', '#about'], ['Login', '/login']].map(([l, h]) => (
            <a key={l} href={h} className={cls('block py-2 text-sm font-medium', dark ? 'text-gray-300' : 'text-gray-700')} onClick={() => setMenuOpen(false)}>{l}</a>
          ))}
          <button onClick={() => { toggleDark(); setMenuOpen(false); }} className={cls('block py-2 text-sm font-medium', dark ? 'text-gray-300' : 'text-gray-700')}>{dark ? 'Light Mode' : 'Dark Mode'}</button>
          <a href={SIGNUP} className="block text-center py-2 bg-violet-600 text-white text-sm font-semibold rounded-full" onClick={() => setMenuOpen(false)}>Start Free</a>
        </div>
      )}
    </nav>
  );
});

/* ================================================================== */
/*  3. HERO SECTION                                                    */
/* ================================================================== */
const HERO_HEADLINE = 'Your Retail Empire. AI-Powered.';
const HERO_SUBTITLE = 'WhatsApp commerce. Smart invoicing. AI support. Inventory. Loyalty. All unified.';
const CHAT_MESSAGES = [
  { from: 'user', text: 'Hi! I want to check my order status' },
  { from: 'bot', text: 'Hello! I found your order #4821. It\'s out for delivery and should arrive by 4 PM today.' },
  { from: 'user', text: 'Can I change the delivery address?' },
  { from: 'bot', text: 'Sure! Please share the new address and I\'ll update it instantly.' },
  { from: 'user', text: '42 MG Road, Bangalore 560001' },
  { from: 'bot', text: 'Done! Address updated. You\'ll receive a confirmation on WhatsApp shortly.' },
];
const CANNED_RESPONSES: Record<string, string> = {
  'order status': 'Your latest order #5912 shipped yesterday. Tracking: bit.ly/track5912',
  'return': 'We have a 30-day return policy. I can initiate a return right now if you\'d like!',
  'recommend': 'Based on your purchase history, you might love our new Silk Collection. Shall I share the catalog?',
  'help': 'I can help with orders, returns, payments, loyalty points, and product recommendations!',
};

const HeroSection = memo(({ dark: _dark }: { dark: boolean }) => {
  const [typedLen, setTypedLen] = useState(0);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [chatIdx, setChatIdx] = useState(0);
  const [showTyping, setShowTyping] = useState(false);
  const [chatDone, setChatDone] = useState(false);
  const [userMsg, setUserMsg] = useState('');
  const [extraMsgs, setExtraMsgs] = useState<{ from: string; text: string }[]>([]);
  const [sparkles, setSparkles] = useState<{ id: number; x: number; y: number }[]>([]);
  const heroRef = useRef<HTMLDivElement>(null);
  const sparkleId = useRef(0);

  // Typing headline
  useEffect(() => {
    if (typedLen >= HERO_HEADLINE.length) return;
    const t = setTimeout(() => setTypedLen(l => l + 1), 60);
    return () => clearTimeout(t);
  }, [typedLen]);

  // Auto-play chat
  useEffect(() => {
    if (chatIdx >= CHAT_MESSAGES.length) { setChatDone(true); return; }
    const delay = chatIdx === 0 ? 1500 : 2000;
    const t1 = setTimeout(() => {
      if (CHAT_MESSAGES[chatIdx].from === 'bot') setShowTyping(true);
      const t2 = setTimeout(() => {
        setShowTyping(false);
        setChatIdx(i => i + 1);
      }, CHAT_MESSAGES[chatIdx].from === 'bot' ? 1200 : 300);
      return () => clearTimeout(t2);
    }, delay);
    return () => clearTimeout(t1);
  }, [chatIdx]);

  const handleMouse = useCallback((e: React.MouseEvent) => {
    if (!heroRef.current) return;
    const r = heroRef.current.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    setMousePos({ x, y });
    // Sparkle trail
    sparkleId.current++;
    setSparkles(prev => [...prev.slice(-8), { id: sparkleId.current, x, y }]);
  }, []);

  useEffect(() => {
    if (sparkles.length === 0) return;
    const t = setTimeout(() => setSparkles(s => s.slice(1)), 400);
    return () => clearTimeout(t);
  }, [sparkles]);

  const handleUserSend = useCallback(() => {
    if (!userMsg.trim()) return;
    const msg = userMsg.trim().toLowerCase();
    setExtraMsgs(prev => [...prev, { from: 'user', text: userMsg.trim() }]);
    setUserMsg('');
    const key = Object.keys(CANNED_RESPONSES).find(k => msg.includes(k));
    setTimeout(() => {
      setExtraMsgs(prev => [...prev, { from: 'bot', text: key ? CANNED_RESPONSES[key] : 'Great question! Our AI can handle that. Sign up to experience it live!' }]);
    }, 800);
  }, [userMsg]);

  const allMessages = [...CHAT_MESSAGES.slice(0, chatIdx), ...extraMsgs];

  return (
    <section
      ref={heroRef}
      onMouseMove={handleMouse}
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16"
      style={{ background: 'radial-gradient(ellipse at 50% 0%, #1e1040 0%, #0c0a1a 50%, #09090b 100%)' }}
    >
      {/* Animated gradient mesh */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-[600px] h-[600px] rounded-full opacity-20 blur-3xl" style={{ background: 'radial-gradient(circle, #7c3aed 0%, transparent 70%)', top: '10%', left: '20%', animation: 'meshMove1 12s ease-in-out infinite' }} />
        <div className="absolute w-[500px] h-[500px] rounded-full opacity-15 blur-3xl" style={{ background: 'radial-gradient(circle, #6d28d9 0%, transparent 70%)', top: '40%', right: '10%', animation: 'meshMove2 15s ease-in-out infinite' }} />
        <div className="absolute w-[400px] h-[400px] rounded-full opacity-10 blur-3xl" style={{ background: 'radial-gradient(circle, #8b5cf6 0%, transparent 70%)', bottom: '10%', left: '50%', animation: 'meshMove3 10s ease-in-out infinite' }} />
      </div>

      {/* Mouse glow */}
      <div
        className="absolute pointer-events-none transition-opacity duration-300"
        style={{
          left: mousePos.x - 80,
          top: mousePos.y - 80,
          width: 160,
          height: 160,
          background: 'radial-gradient(circle, rgba(139,92,246,0.25) 0%, transparent 70%)',
          filter: 'blur(30px)',
          opacity: mousePos.x ? 1 : 0,
        }}
      />

      {/* Sparkle trail */}
      {sparkles.map(s => (
        <div key={s.id} className="absolute pointer-events-none w-2 h-2 rounded-full bg-violet-400" style={{ left: s.x - 4, top: s.y - 4, animation: 'sparkle 0.5s ease-out forwards', boxShadow: '0 0 6px 2px rgba(139,92,246,0.6)' }} />
      ))}

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
        {/* Left: text */}
        <div className="flex-1 text-center lg:text-left">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight tracking-tight">
            {HERO_HEADLINE.slice(0, typedLen)}
            <span className="inline-block w-[3px] h-[1em] bg-violet-400 ml-1 align-middle" style={{ animation: 'blink 1s step-end infinite' }} />
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-gray-400 max-w-xl mx-auto lg:mx-0">{HERO_SUBTITLE}</p>

          {/* Floating stats */}
          <div className="mt-8 flex flex-wrap justify-center lg:justify-start gap-4">
            {[['15,000+', 'messages'], ['94%', 'AI accuracy'], ['< 2s', 'response']].map(([v, l]) => (
              <div key={l} className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm" style={{ animation: 'float 4s ease-in-out infinite' }}>
                <div className="text-white font-bold text-lg">{v}</div>
                <div className="text-gray-500 text-xs">{l}</div>
              </div>
            ))}
          </div>

          {/* CTAs */}
          <div className="mt-10 flex flex-wrap justify-center lg:justify-start gap-4">
            <a href={SIGNUP} className="px-8 py-3.5 bg-violet-600 hover:bg-violet-700 text-white font-semibold rounded-full transition-all shadow-xl shadow-violet-600/30 hover:shadow-violet-600/50 flex items-center gap-2">
              Start Free <IconArrowRight />
            </a>
            <a href="#features" className="px-8 py-3.5 border border-white/20 text-white hover:bg-white/5 font-semibold rounded-full transition-all flex items-center gap-2">
              See it in action <span className="text-xs">&darr;</span>
            </a>
          </div>
        </div>

        {/* Right: Phone mockup */}
        <div className="flex-shrink-0 w-72 sm:w-80">
          <div className="relative mx-auto w-64 sm:w-72 rounded-[2.5rem] bg-gray-900 border-4 border-gray-700 shadow-2xl shadow-violet-900/30 overflow-hidden" style={{ animation: 'pulseGlow 4s ease-in-out infinite' }}>
            {/* Phone notch */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-gray-900 rounded-b-2xl z-10" />
            {/* Chat area */}
            <div className="bg-gradient-to-b from-emerald-900/50 to-gray-900 pt-10 pb-3 px-3 min-h-[420px] flex flex-col">
              <div className="flex items-center gap-2 pb-3 border-b border-gray-700 mb-3">
                <div className="w-8 h-8 rounded-full bg-violet-600 flex items-center justify-center"><IconStore /></div>
                <div><div className="text-white text-sm font-semibold">AI Commerce OS</div><div className="text-green-400 text-[10px]">Online</div></div>
              </div>
              <div className="flex-1 overflow-y-auto space-y-2 max-h-[280px] scrollbar-thin">
                {allMessages.map((m, i) => (
                  <div key={i} className={cls('max-w-[85%] px-3 py-2 rounded-xl text-xs', m.from === 'user' ? 'ml-auto bg-emerald-700 text-white rounded-br-sm' : 'bg-gray-800 text-gray-200 rounded-bl-sm')} style={{ animation: m.from === 'user' ? 'slideInRight 0.3s ease' : 'slideInLeft 0.3s ease' }}>
                    {m.text}
                  </div>
                ))}
                {showTyping && (
                  <div className="flex gap-1 px-3 py-2 bg-gray-800 rounded-xl rounded-bl-sm w-fit">
                    {[0, 1, 2].map(i => <div key={i} className="w-2 h-2 bg-gray-500 rounded-full" style={{ animation: `typingDot 1s ease-in-out ${i * 0.15}s infinite` }} />)}
                  </div>
                )}
              </div>
              {chatDone && (
                <div className="mt-2 flex gap-2">
                  <input
                    type="text"
                    value={userMsg}
                    onChange={e => setUserMsg(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleUserSend()}
                    placeholder="Type a message..."
                    className="flex-1 bg-gray-800 border border-gray-700 rounded-full px-3 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-violet-500"
                  />
                  <button onClick={handleUserSend} className="p-2 bg-violet-600 rounded-full text-white hover:bg-violet-700 transition-colors"><IconSend /></button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

/* ================================================================== */
/*  4. LIVE ACTIVITY TICKER                                            */
/* ================================================================== */
const TICKER_ITEMS = [
  'Franfra Candles sent 23 campaigns',
  'StyleCorp resolved 15 AI tickets',
  'TechGear created \u20B94.2L invoices',
  'BeautyBox enrolled 50 loyalty members',
  'UrbanBites processed 340 orders via WhatsApp',
  'PureGlow automated 89% of support queries',
  'HomeCraft tracked \u20B98.1L revenue this week',
  'FitZone onboarded 200 new contacts',
];

const LiveTicker = memo(() => {
  const items = [...TICKER_ITEMS, ...TICKER_ITEMS];
  return (
    <div className="bg-violet-600 py-3 overflow-hidden relative">
      <div className="flex whitespace-nowrap" style={{ animation: 'marquee 30s linear infinite' }}>
        {items.map((t, i) => (
          <span key={i} className="text-white text-sm font-medium mx-8 flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-white rounded-full opacity-60" />
            {t}
          </span>
        ))}
      </div>
    </div>
  );
});

/* ================================================================== */
/*  5. AI vs HUMAN RACE                                                */
/* ================================================================== */
const CUSTOMER_QUERY = 'Hi, I ordered a red dress but received blue. What should I do?';
const HUMAN_RESPONSE_STEPS = [
  { text: 'Let me check your ordr...', delay: 2000 },
  { text: 'Let me check your order...', delay: 3500 },
  { text: 'Let me check your order... I see order #3847. ', delay: 5000 },
  { text: 'Let me check your order... I see order #3847. Sorry about that! Please send the item back and we will process a refund or exchange within 5-7 business days.', delay: 8200 },
];
const AI_RESPONSE = 'I found your order #3847. I\'m sorry about the mix-up! I\'ve already initiated a return pickup for tomorrow between 10 AM - 1 PM. Here\'s your return label: [Return Link]. Would you prefer a refund or exchange? Either way, it\'ll be processed within 24 hours.';

const AIvsHumanRace = memo(({ dark }: { dark: boolean }) => {
  const { ref, inView } = useInView(0.3);
  const [racing, setRacing] = useState(false);
  const [humanStep, setHumanStep] = useState(0);
  const [aiTyped, setAiTyped] = useState(0);
  const [humanTime, setHumanTime] = useState(0);
  const [aiTime, setAiTime] = useState(0);
  const [humanDone, setHumanDone] = useState(false);
  const [aiDone, setAiDone] = useState(false);
  const startRef = useRef(0);

  const startRace = useCallback(() => {
    setRacing(true);
    setHumanStep(0);
    setAiTyped(0);
    setHumanTime(0);
    setAiTime(0);
    setHumanDone(false);
    setAiDone(false);
    startRef.current = Date.now();
  }, []);

  useEffect(() => {
    if (inView && !racing && humanTime === 0) startRace();
  }, [inView, racing, humanTime, startRace]);

  // Human typing
  useEffect(() => {
    if (!racing || humanDone) return;
    if (humanStep >= HUMAN_RESPONSE_STEPS.length) { setHumanDone(true); setHumanTime(8.2); return; }
    const t = setTimeout(() => setHumanStep(s => s + 1), HUMAN_RESPONSE_STEPS[humanStep].delay - (humanStep > 0 ? HUMAN_RESPONSE_STEPS[humanStep - 1].delay : 0));
    return () => clearTimeout(t);
  }, [racing, humanStep, humanDone]);

  // AI typing
  useEffect(() => {
    if (!racing || aiDone) return;
    if (aiTyped >= AI_RESPONSE.length) { setAiDone(true); setAiTime(1.5); return; }
    const t = setTimeout(() => setAiTyped(n => n + 3), 1500 / AI_RESPONSE.length * 3);
    return () => clearTimeout(t);
  }, [racing, aiTyped, aiDone]);

  // Timer display
  useEffect(() => {
    if (!racing) return;
    if (humanDone && aiDone) { setRacing(false); return; }
    const iv = setInterval(() => {
      const elapsed = (Date.now() - startRef.current) / 1000;
      if (!humanDone) setHumanTime(Math.min(elapsed, 8.2));
      if (!aiDone) setAiTime(Math.min(elapsed, 1.5));
    }, 50);
    return () => clearInterval(iv);
  }, [racing, humanDone, aiDone]);

  return (
    <section ref={ref} className={cls('py-20 px-4', dark ? 'bg-gray-900' : 'bg-gray-50')} id="features">
      <div className="max-w-5xl mx-auto">
        <h2 className={cls('text-3xl sm:text-4xl font-bold text-center mb-4', dark ? 'text-white' : 'text-gray-900')}>See the Difference</h2>
        <p className={cls('text-center mb-10 max-w-xl mx-auto', dark ? 'text-gray-400' : 'text-gray-600')}>A customer sends: "{CUSTOMER_QUERY}"</p>
        <div className="grid md:grid-cols-2 gap-6">
          {/* Human */}
          <div className={cls('rounded-2xl p-6 border', dark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200')}>
            <div className="flex items-center justify-between mb-4">
              <span className={cls('font-semibold', dark ? 'text-gray-300' : 'text-gray-700')}>Human Agent</span>
              <span className="text-orange-400 font-mono text-lg font-bold">{humanTime.toFixed(1)}s</span>
            </div>
            <div className={cls('min-h-[120px] text-sm rounded-lg p-4', dark ? 'bg-gray-900 text-gray-300' : 'bg-gray-50 text-gray-700')}>
              {humanStep > 0 ? HUMAN_RESPONSE_STEPS[Math.min(humanStep, HUMAN_RESPONSE_STEPS.length) - 1].text : ''}
              {!humanDone && racing && <span className="inline-block w-[2px] h-4 bg-orange-400 ml-0.5 align-middle" style={{ animation: 'blink 0.7s step-end infinite' }} />}
            </div>
          </div>
          {/* AI */}
          <div className={cls('rounded-2xl p-6 border-2 border-violet-500/50', dark ? 'bg-gray-800' : 'bg-white')}>
            <div className="flex items-center justify-between mb-4">
              <span className="text-violet-400 font-semibold">AI Agent</span>
              <span className="text-violet-400 font-mono text-lg font-bold">{aiTime.toFixed(1)}s</span>
            </div>
            <div className={cls('min-h-[120px] text-sm rounded-lg p-4', dark ? 'bg-gray-900 text-gray-300' : 'bg-gray-50 text-gray-700')}>
              {AI_RESPONSE.slice(0, aiTyped)}
              {!aiDone && racing && <span className="inline-block w-[2px] h-4 bg-violet-400 ml-0.5 align-middle" style={{ animation: 'blink 0.7s step-end infinite' }} />}
            </div>
          </div>
        </div>
        {humanDone && aiDone && (
          <div className="text-center mt-8" style={{ animation: 'fadeInUp 0.5s ease' }}>
            <p className={cls('text-lg font-semibold mb-4', dark ? 'text-white' : 'text-gray-900')}>
              AI Commerce OS handles support <span className="text-violet-400">5x faster</span>. Zero typos.
            </p>
            <button onClick={startRace} className="px-5 py-2 border border-violet-500 text-violet-400 rounded-full text-sm font-medium hover:bg-violet-500/10 transition-colors flex items-center gap-2 mx-auto">
              <IconRefresh /> Replay
            </button>
          </div>
        )}
      </div>
    </section>
  );
});

/* ================================================================== */
/*  6. BUILD YOUR STORE IN 60 SECONDS                                  */
/* ================================================================== */
const CATEGORIES = ['Fashion', 'Food', 'Beauty', 'Electronics', 'Home'];
const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  Fashion: <IconTag />,
  Food: <IconPackage />,
  Beauty: <IconHeart />,
  Electronics: <IconZap />,
  Home: <IconStore />,
};

const BuildStoreChallenge = memo(({ dark }: { dark: boolean }) => {
  const { ref, inView } = useInView();
  const [started, setStarted] = useState(false);
  const [timer, setTimer] = useState(60);
  const [step, setStep] = useState(1);
  const [storeName, setStoreName] = useState('');
  const [category, setCategory] = useState('');
  const [productName, setProductName] = useState('');
  const [productPrice, setProductPrice] = useState('');
  const [campaign, setCampaign] = useState('');
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    if (!started || finished) return;
    if (timer <= 0) { setFinished(true); return; }
    const iv = setInterval(() => {
      setTimer(t => {
        const next = t - 1;
        if (next <= 45 && step < 4) setStep(4);
        else if (next <= 30 && step < 3) setStep(3);
        else if (next <= 15 && step < 2) setStep(2);
        if (next <= 0) setFinished(true);
        return Math.max(0, next);
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [started, finished, timer, step]);

  const handleStart = () => { setStarted(true); setTimer(60); setStep(1); setFinished(false); setStoreName(''); setCategory(''); setProductName(''); setProductPrice(''); setCampaign(''); };

  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(`Just built my AI store in 60 seconds on AI Commerce OS! Try it: https://www.idliapam.in`)}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`Just built my AI store in 60 seconds on AI Commerce OS! Try it: https://www.idliapam.in`)}`;

  return (
    <section ref={ref} className={cls('py-20 px-4', dark ? 'bg-gray-950' : 'bg-white')}>
      <div className="max-w-3xl mx-auto text-center">
        <h2 className={cls('text-3xl sm:text-4xl font-bold mb-4', dark ? 'text-white' : 'text-gray-900', inView && 'animate-[fadeInUp_0.6s_ease]')}>Can you build a store in 60 seconds?</h2>

        {!started && !finished && (
          <button onClick={handleStart} className="mt-8 px-8 py-4 bg-violet-600 hover:bg-violet-700 text-white font-bold text-lg rounded-full transition-all shadow-xl shadow-violet-600/30 hover:shadow-violet-600/50">
            Start Challenge
          </button>
        )}

        {started && !finished && (
          <div className="mt-8 space-y-8">
            {/* Timer */}
            <div className="relative inline-flex items-center justify-center w-28 h-28 rounded-full border-4 border-violet-500" style={{ animation: timer <= 10 ? 'shake 0.5s ease infinite' : undefined }}>
              <span className={cls('text-4xl font-mono font-bold', timer <= 10 ? 'text-red-400' : (dark ? 'text-white' : 'text-gray-900'))}>{timer}</span>
            </div>
            <div className="flex justify-center gap-2 mb-6">
              {[1, 2, 3, 4].map(s => (
                <div key={s} className={cls('w-3 h-3 rounded-full transition-all', s <= step ? 'bg-violet-500 scale-110' : (dark ? 'bg-gray-700' : 'bg-gray-300'))} />
              ))}
            </div>

            <div className={cls('text-left max-w-md mx-auto rounded-2xl p-6 border', dark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200')} style={{ animation: 'fadeInUp 0.4s ease' }}>
              {step === 1 && (
                <div>
                  <label className={cls('block text-sm font-semibold mb-2', dark ? 'text-gray-300' : 'text-gray-700')}>Name your store</label>
                  <input type="text" value={storeName} onChange={e => setStoreName(e.target.value)} placeholder="e.g. Urban Threads" className={cls('w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-violet-500', dark ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400')} autoFocus />
                  <button onClick={() => setStep(2)} className="mt-4 px-6 py-2 bg-violet-600 text-white rounded-full text-sm font-medium hover:bg-violet-700 transition-colors">Next</button>
                </div>
              )}
              {step === 2 && (
                <div>
                  <label className={cls('block text-sm font-semibold mb-3', dark ? 'text-gray-300' : 'text-gray-700')}>Pick your category</label>
                  <div className="grid grid-cols-3 gap-2">
                    {CATEGORIES.map(c => (
                      <button key={c} onClick={() => { setCategory(c); setStep(3); }} className={cls('px-3 py-3 rounded-xl border text-sm font-medium transition-all flex flex-col items-center gap-1', category === c ? 'bg-violet-600 border-violet-600 text-white' : (dark ? 'bg-gray-900 border-gray-600 text-gray-300 hover:border-violet-500' : 'bg-white border-gray-300 text-gray-700 hover:border-violet-500'))}>
                        <span className="opacity-70">{CATEGORY_ICONS[c]}</span>
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {step === 3 && (
                <div>
                  <label className={cls('block text-sm font-semibold mb-2', dark ? 'text-gray-300' : 'text-gray-700')}>Add a product</label>
                  <input type="text" value={productName} onChange={e => setProductName(e.target.value)} placeholder="Product name" className={cls('w-full px-4 py-3 rounded-xl border text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-violet-500', dark ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400')} />
                  <input type="text" value={productPrice} onChange={e => setProductPrice(e.target.value)} placeholder="Price (e.g. 999)" className={cls('w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-violet-500', dark ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400')} />
                  <button onClick={() => setStep(4)} className="mt-4 px-6 py-2 bg-violet-600 text-white rounded-full text-sm font-medium hover:bg-violet-700 transition-colors">Next</button>
                </div>
              )}
              {step === 4 && (
                <div>
                  <label className={cls('block text-sm font-semibold mb-2', dark ? 'text-gray-300' : 'text-gray-700')}>Set your first campaign</label>
                  <textarea value={campaign} onChange={e => setCampaign(e.target.value)} placeholder="Welcome message for new customers..." rows={3} className={cls('w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none', dark ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400')} />
                  <button onClick={() => setFinished(true)} className="mt-4 px-6 py-2 bg-violet-600 text-white rounded-full text-sm font-medium hover:bg-violet-700 transition-colors">Finish!</button>
                </div>
              )}
            </div>
          </div>
        )}

        {finished && (() => {
          const themeColors: Record<string, string> = {
            Fashion: 'from-pink-500 to-violet-600',
            Food: 'from-amber-500 to-red-500',
            Beauty: 'from-rose-400 to-purple-500',
            Electronics: 'from-blue-500 to-cyan-400',
            Home: 'from-emerald-500 to-teal-500',
          };
          const gradient = themeColors[category] || 'from-violet-500 to-blue-500';
          const initials = (storeName || 'MY').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

          return (
            <div className="mt-8" style={{ animation: 'celebration 0.6s ease forwards' }}>
              {/* Visual Store Preview Card */}
              <div className="max-w-sm mx-auto" style={{ perspective: '1000px' }}>
                <div className="rounded-2xl overflow-hidden shadow-2xl shadow-violet-500/20 border border-violet-500/30" style={{ animation: 'fadeInUp 0.8s ease' }}>
                  {/* Store Header */}
                  <div className={`bg-gradient-to-r ${gradient} p-4 flex items-center justify-between`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center text-white font-bold text-sm">{initials}</div>
                      <span className="text-white font-bold text-lg">{storeName || 'My Store'}</span>
                    </div>
                    <div className="flex gap-2">
                      <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center"><svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></div>
                      <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center"><svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/></svg></div>
                    </div>
                  </div>

                  {/* Product Section */}
                  <div className={cls('p-5', dark ? 'bg-gray-800' : 'bg-white')}>
                    <div className="flex gap-4">
                      {/* Product Image Placeholder */}
                      <div className={`w-28 h-28 rounded-xl bg-gradient-to-br ${gradient} opacity-80 flex items-center justify-center flex-shrink-0`}>
                        <IconPackage />
                      </div>
                      <div className="flex-1">
                        <div className={cls('text-xs font-medium uppercase tracking-wider mb-1', dark ? 'text-gray-500' : 'text-gray-400')}>{category || 'General'} Store</div>
                        <div className={cls('text-lg font-bold mb-1', dark ? 'text-white' : 'text-gray-900')}>{productName || 'Product'}</div>
                        <div className="text-violet-500 font-bold text-xl mb-2">{'\u20B9'}{productPrice || '0'}</div>
                        <div className="flex items-center gap-1">
                          {[1,2,3,4,5].map(i => <svg key={i} className="w-3.5 h-3.5 text-amber-400" viewBox="0 0 20 20" fill="currentColor"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>)}
                          <span className={cls('text-xs ml-1', dark ? 'text-gray-500' : 'text-gray-400')}>(42 reviews)</span>
                        </div>
                      </div>
                    </div>

                    {/* CTA Button */}
                    <button className={`w-full mt-4 py-2.5 bg-gradient-to-r ${gradient} text-white font-semibold rounded-xl text-sm`}>Add to Cart</button>

                    {/* Feature Bar */}
                    <div className={cls('flex justify-between mt-4 pt-3 border-t text-[11px]', dark ? 'border-gray-700 text-gray-500' : 'border-gray-100 text-gray-400')}>
                      <span className="flex items-center gap-1"><IconTrendingUp /> Trending</span>
                      <span className="flex items-center gap-1"><IconWhatsApp /> WhatsApp</span>
                      <span className="flex items-center gap-1"><IconHeart /> Loyalty</span>
                      <span className="flex items-center gap-1"><IconZap /> AI</span>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className={cls('px-4 py-2 text-center text-[10px]', dark ? 'bg-gray-900 text-gray-600' : 'bg-gray-50 text-gray-400')}>
                    Powered by <span className="text-violet-500 font-semibold">AI Commerce OS</span>
                  </div>
                </div>
              </div>

              {/* Share Buttons */}
              <p className={cls('text-sm mt-6 mb-3', dark ? 'text-gray-400' : 'text-gray-500')}>Show off your store</p>
              <div className="flex flex-wrap justify-center gap-3">
                <a href={twitterUrl} target="_blank" rel="noopener noreferrer" className="px-5 py-2.5 bg-gray-900 text-white rounded-full text-sm font-medium flex items-center gap-2 hover:bg-gray-800 transition-all hover:scale-105"><IconTwitter /> Share on X</a>
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="px-5 py-2.5 bg-green-600 text-white rounded-full text-sm font-medium flex items-center gap-2 hover:bg-green-700 transition-all hover:scale-105"><IconWhatsApp /> Share on WhatsApp</a>
                <a href={SIGNUP} className="px-5 py-2.5 bg-violet-600 text-white rounded-full text-sm font-medium flex items-center gap-2 hover:bg-violet-700 transition-all hover:scale-105">Get Started for Real <IconArrowRight /></a>
              </div>
              <button onClick={handleStart} className={cls('mt-4 text-sm font-medium flex items-center gap-1 mx-auto', dark ? 'text-gray-500 hover:text-violet-400' : 'text-gray-400 hover:text-violet-600')}><IconRefresh /> Try again</button>
            </div>
          );
        })()}
      </div>
    </section>
  );
});

/* ================================================================== */
/*  7. INTERACTIVE SIMULATOR                                           */
/* ================================================================== */
const SimulatorTabs = memo(({ dark }: { dark: boolean }) => {
  const { ref, inView } = useInView();
  const [tab, setTab] = useState(0);
  const tabs = ['WhatsApp AI', 'Invoice Creator', 'Loyalty Card', 'AI Support'];

  return (
    <section ref={ref} className={cls('py-20 px-4', dark ? 'bg-gray-900' : 'bg-gray-50')} id="simulator">
      <div className="max-w-4xl mx-auto">
        <h2 className={cls('text-3xl sm:text-4xl font-bold text-center mb-4', dark ? 'text-white' : 'text-gray-900', inView && 'animate-[fadeInUp_0.6s_ease]')}>Don't just read about it. Try it.</h2>
        <div className="flex justify-center flex-wrap gap-2 mt-8 mb-8">
          {tabs.map((t, i) => (
            <button key={t} onClick={() => setTab(i)} className={cls('px-5 py-2.5 rounded-full text-sm font-medium transition-all', tab === i ? 'bg-violet-600 text-white shadow-lg shadow-violet-600/25' : (dark ? 'bg-gray-800 text-gray-400 hover:text-white' : 'bg-white text-gray-600 hover:text-gray-900 border border-gray-200'))}>
              {t}
            </button>
          ))}
        </div>
        <div className={cls('rounded-2xl border overflow-hidden', dark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200')} style={inView ? { animation: 'fadeInUp 0.5s ease' } : {}}>
          {tab === 0 && <WhatsAppSim dark={dark} />}
          {tab === 1 && <InvoiceSim dark={dark} />}
          {tab === 2 && <LoyaltySim dark={dark} />}
          {tab === 3 && <SupportSim dark={dark} />}
        </div>
      </div>
    </section>
  );
});

const WhatsAppSim = ({ dark }: { dark: boolean }) => {
  const [msgs, setMsgs] = useState<{ from: string; text: string }[]>([{ from: 'bot', text: 'Hi! How can I help you today?' }]);
  const [input, setInput] = useState('');
  const suggestions = ['Order status', 'Return policy', 'Recommendations'];
  const responses: Record<string, string> = {
    'order status': 'Your order #7291 was shipped on April 1. Expected delivery: April 4. Track here: bit.ly/t7291',
    'return policy': 'We offer 30-day hassle-free returns. Items must be unused with tags intact. I can start a return for you now!',
    'recommendations': 'Based on your recent purchase, here are top picks: 1) Silk Kurta Set (Rs 2,499) 2) Cotton Palazzo (Rs 1,299) 3) Printed Dupatta (Rs 699)',
  };
  const send = (text: string) => {
    if (!text.trim()) return;
    const t = text.trim();
    setMsgs(m => [...m, { from: 'user', text: t }]);
    setInput('');
    const key = Object.keys(responses).find(k => t.toLowerCase().includes(k));
    setTimeout(() => {
      setMsgs(m => [...m, { from: 'bot', text: key ? responses[key] : 'I can help with orders, returns, and recommendations. Try asking about one of those!' }]);
    }, 600);
  };
  return (
    <div className="p-6">
      <div className="space-y-3 min-h-[200px] max-h-[300px] overflow-y-auto mb-4">
        {msgs.map((m, i) => (
          <div key={i} className={cls('max-w-[80%] px-4 py-2.5 rounded-xl text-sm', m.from === 'user' ? 'ml-auto bg-violet-600 text-white rounded-br-sm' : (dark ? 'bg-gray-700 text-gray-200 rounded-bl-sm' : 'bg-gray-100 text-gray-800 rounded-bl-sm'))} style={{ animation: 'fadeInUp 0.3s ease' }}>
            {m.text}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        {suggestions.map(s => (
          <button key={s} onClick={() => send(s)} className={cls('px-3 py-1.5 rounded-full text-xs font-medium border transition-colors', dark ? 'border-gray-600 text-gray-400 hover:text-white hover:border-violet-500' : 'border-gray-300 text-gray-600 hover:text-violet-600 hover:border-violet-500')}>
            {s}
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        <input type="text" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && send(input)} placeholder="Type a message..." className={cls('flex-1 px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-violet-500', dark ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400')} />
        <button onClick={() => send(input)} className="px-4 py-2.5 bg-violet-600 text-white rounded-xl hover:bg-violet-700 transition-colors"><IconSend /></button>
      </div>
    </div>
  );
};

const InvoiceSim = ({ dark }: { dark: boolean }) => {
  const [name, setName] = useState('');
  const [items, setItems] = useState([{ desc: '', qty: '1', rate: '' }]);
  const [created, setCreated] = useState(false);
  const subtotal = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.rate) || 0), 0);
  const gst = subtotal * 0.18;
  const total = subtotal + gst;
  const addItem = () => setItems(i => [...i, { desc: '', qty: '1', rate: '' }]);
  const updateItem = (idx: number, field: string, val: string) => setItems(i => i.map((it, j) => j === idx ? { ...it, [field]: val } : it));
  return (
    <div className="p-6">
      {!created ? (
        <div className="space-y-4">
          <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Customer name" className={cls('w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-violet-500', dark ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400')} />
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-3 gap-2">
              <input type="text" value={it.desc} onChange={e => updateItem(i, 'desc', e.target.value)} placeholder="Item" className={cls('px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-violet-500', dark ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400')} />
              <input type="number" value={it.qty} onChange={e => updateItem(i, 'qty', e.target.value)} placeholder="Qty" className={cls('px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-violet-500', dark ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400')} />
              <input type="number" value={it.rate} onChange={e => updateItem(i, 'rate', e.target.value)} placeholder="Rate" className={cls('px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-violet-500', dark ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400')} />
            </div>
          ))}
          <button onClick={addItem} className={cls('text-sm font-medium', dark ? 'text-violet-400' : 'text-violet-600')}>+ Add item</button>
          <div className={cls('text-right space-y-1 text-sm', dark ? 'text-gray-300' : 'text-gray-700')}>
            <div>Subtotal: {'\u20B9'}{subtotal.toFixed(2)}</div>
            <div>GST (18%): {'\u20B9'}{gst.toFixed(2)}</div>
            <div className="font-bold text-lg">Total: {'\u20B9'}{total.toFixed(2)}</div>
          </div>
          <button onClick={() => setCreated(true)} className="w-full py-3 bg-violet-600 text-white rounded-xl font-semibold hover:bg-violet-700 transition-colors">Create Invoice</button>
        </div>
      ) : (
        <div className="text-center py-8" style={{ animation: 'scaleReveal 0.5s ease' }}>
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="w-8 h-8 text-green-400" style={{ strokeDasharray: 24, animation: 'checkDraw 0.5s ease 0.2s forwards', strokeDashoffset: 24 }}><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <h3 className={cls('text-xl font-bold mb-2', dark ? 'text-white' : 'text-gray-900')}>Invoice Created!</h3>
          <p className={cls('text-sm', dark ? 'text-gray-400' : 'text-gray-600')}>INV-{Math.floor(Math.random() * 9000 + 1000)} for {name || 'Customer'} — {'\u20B9'}{total.toFixed(2)}</p>
          <button onClick={() => setCreated(false)} className="mt-4 text-violet-400 text-sm font-medium hover:underline">Create another</button>
        </div>
      )}
    </div>
  );
};

const LoyaltySim = ({ dark }: { dark: boolean }) => {
  const [name, setName] = useState('');
  const [generated, setGenerated] = useState(false);
  const [flipped, setFlipped] = useState(false);
  const memId = useMemo(() => 'MEM-' + Math.floor(Math.random() * 900000 + 100000), []);
  return (
    <div className="p-6">
      {!generated ? (
        <div className="max-w-sm mx-auto space-y-4">
          <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Enter your name" className={cls('w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-violet-500', dark ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400')} />
          <button onClick={() => name.trim() && setGenerated(true)} className="w-full py-3 bg-violet-600 text-white rounded-xl font-semibold hover:bg-violet-700 transition-colors">Generate Card</button>
        </div>
      ) : (
        <div className="flex flex-col items-center py-4">
          <div className="relative w-80 h-48 cursor-pointer" style={{ perspective: '600px' }} onClick={() => setFlipped(!flipped)}>
            <div className="absolute inset-0 transition-transform duration-700" style={{ transformStyle: 'preserve-3d', transform: flipped ? 'rotateY(180deg)' : 'rotateY(0)' }}>
              {/* Front */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-700 p-6 text-white shadow-xl" style={{ backfaceVisibility: 'hidden' }}>
                <div className="text-xs font-medium opacity-70 mb-1">LOYALTY MEMBER</div>
                <div className="text-lg font-bold">{name}</div>
                <div className="mt-2 font-mono text-sm opacity-80">{memId}</div>
                <div className="mt-4 flex items-center gap-2">
                  <span className="px-3 py-1 bg-yellow-400/20 text-yellow-300 rounded-full text-xs font-bold">GOLD</span>
                  <div className="flex">{[0,1,2,3,4].map(i => <IconStar key={i} />)}</div>
                </div>
                <div className="absolute bottom-4 right-6 text-xs opacity-50">Tap to flip</div>
              </div>
              {/* Back */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-gray-800 to-gray-900 p-6 flex items-center justify-center shadow-xl" style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}>
                <div className="grid grid-cols-8 grid-rows-8 gap-1 opacity-60">
                  {Array.from({ length: 64 }).map((_, i) => (
                    <div key={i} className={cls('w-3 h-3 rounded-sm', Math.random() > 0.4 ? 'bg-violet-400' : 'bg-transparent')} />
                  ))}
                </div>
              </div>
            </div>
          </div>
          <p className={cls('mt-4 text-sm', dark ? 'text-gray-400' : 'text-gray-600')}>Click the card to flip it</p>
        </div>
      )}
    </div>
  );
};

const SupportSim = ({ dark }: { dark: boolean }) => {
  const [submitted, setSubmitted] = useState(false);
  const [resolved, setResolved] = useState(false);
  const [issue, setIssue] = useState('');
  const aiSuggestion = 'Based on similar tickets, this appears to be a delivery delay issue. Recommended action: Send tracking update to customer with estimated delivery date and offer 10% discount code for the inconvenience.';
  return (
    <div className="p-6">
      {!submitted ? (
        <div className="space-y-4 max-w-lg mx-auto">
          <textarea value={issue} onChange={e => setIssue(e.target.value)} placeholder="Describe the support issue..." rows={3} className={cls('w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none', dark ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400')} />
          <button onClick={() => issue.trim() && setSubmitted(true)} className="w-full py-3 bg-violet-600 text-white rounded-xl font-semibold hover:bg-violet-700 transition-colors">Submit Ticket</button>
        </div>
      ) : !resolved ? (
        <div className="space-y-4 max-w-lg mx-auto" style={{ animation: 'fadeInUp 0.4s ease' }}>
          <div className={cls('rounded-xl p-4 border', dark ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200')}>
            <div className="flex items-center gap-2 mb-3">
              <IconZap />
              <span className={cls('text-sm font-semibold', dark ? 'text-violet-400' : 'text-violet-600')}>AI Suggestion</span>
              <span className="ml-auto text-xs font-medium text-green-400">92% confidence</span>
            </div>
            <div className="w-full h-2 rounded-full bg-gray-700 mb-3 overflow-hidden">
              <div className="h-full bg-green-400 rounded-full" style={{ '--conf': '92%', animation: 'confidenceFill 1s ease forwards' } as React.CSSProperties} />
            </div>
            <p className={cls('text-sm', dark ? 'text-gray-300' : 'text-gray-700')}>{aiSuggestion}</p>
          </div>
          <button onClick={() => setResolved(true)} className="w-full py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-colors">Apply AI Suggestion</button>
        </div>
      ) : (
        <div className="text-center py-8" style={{ animation: 'scaleReveal 0.5s ease' }}>
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="w-8 h-8 text-green-400" style={{ strokeDasharray: 24, animation: 'checkDraw 0.5s ease 0.2s forwards', strokeDashoffset: 24 }}><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <h3 className={cls('text-xl font-bold mb-2', dark ? 'text-white' : 'text-gray-900')}>Ticket Resolved!</h3>
          <p className={cls('text-sm', dark ? 'text-gray-400' : 'text-gray-600')}>AI resolved the issue in under 3 seconds.</p>
          <button onClick={() => { setSubmitted(false); setResolved(false); setIssue(''); }} className="mt-4 text-violet-400 text-sm font-medium hover:underline">Try another</button>
        </div>
      )}
    </div>
  );
};

/* ================================================================== */
/*  8. CALCULATE YOUR SAVINGS                                          */
/* ================================================================== */
const SavingsCalculator = memo(({ dark }: { dark: boolean }) => {
  const { ref, inView } = useInView();
  const [orders, setOrders] = useState(200);
  const [queries, setQueries] = useState(100);
  const [hours, setHours] = useState(15);
  const savings = Math.round(orders * 12 + queries * 18 + hours * 350);
  const hoursSaved = Math.round(hours * 0.7 * 10) / 10;

  return (
    <section ref={ref} className={cls('py-20 px-4', dark ? 'bg-gray-950' : 'bg-white')}>
      <div className="max-w-3xl mx-auto text-center">
        <h2 className={cls('text-3xl sm:text-4xl font-bold mb-4', dark ? 'text-white' : 'text-gray-900', inView && 'animate-[fadeInUp_0.6s_ease]')}>How much could you save?</h2>
        <div className="mt-10 space-y-8 text-left max-w-lg mx-auto">
          {[
            { label: 'Monthly orders', value: orders, set: setOrders, max: 1000, step: 10 },
            { label: 'Support queries/month', value: queries, set: setQueries, max: 500, step: 10 },
            { label: 'Hours on manual tasks/week', value: hours, set: setHours, max: 40, step: 1 },
          ].map(s => (
            <div key={s.label}>
              <div className="flex justify-between mb-2">
                <span className={cls('text-sm font-medium', dark ? 'text-gray-300' : 'text-gray-700')}>{s.label}</span>
                <span className="text-violet-400 font-bold">{s.value}</span>
              </div>
              <input type="range" min={0} max={s.max} step={s.step} value={s.value} onChange={e => s.set(Number(e.target.value))} className="w-full h-2 rounded-full appearance-none cursor-pointer bg-gray-700 accent-violet-500" />
            </div>
          ))}
        </div>
        <div className="mt-10" style={inView ? { animation: 'scaleReveal 0.6s ease' } : {}}>
          <div className={cls('inline-block rounded-2xl px-10 py-8 border-2 border-violet-500/50', dark ? 'bg-gray-800' : 'bg-violet-50')}>
            <p className={cls('text-lg', dark ? 'text-gray-300' : 'text-gray-700')}>You'd save</p>
            <p className="text-4xl sm:text-5xl font-extrabold text-violet-500 my-2">{'\u20B9'}{savings.toLocaleString()}<span className="text-lg font-normal text-gray-500">/month</span></p>
            <p className={cls('text-lg', dark ? 'text-gray-300' : 'text-gray-700')}>and <span className="text-violet-400 font-bold">{hoursSaved} hours</span>/week</p>
            <p className={cls('text-sm mt-2', dark ? 'text-gray-500' : 'text-gray-400')}>with AI Commerce OS</p>
          </div>
        </div>
      </div>
    </section>
  );
});

/* ================================================================== */
/*  9. LIFECYCLE FLOW - CONVEYOR                                       */
/* ================================================================== */
const LIFECYCLE_STEPS = [
  { icon: <IconGlobe />, title: 'See Ad', desc: 'Customer sees your ad on social media or search.' },
  { icon: <IconWhatsApp />, title: 'WhatsApp "Hi"', desc: 'They message your WhatsApp business number.' },
  { icon: <IconZap />, title: 'AI Responds', desc: 'AI Copilot instantly engages with smart replies.' },
  { icon: <IconPackage />, title: 'Places Order', desc: 'Order placed directly via WhatsApp chat.' },
  { icon: <IconInvoice />, title: 'Gets Invoice', desc: 'Auto-generated GST invoice sent instantly.' },
  { icon: <IconPricing />, title: 'Pays UPI', desc: 'Seamless UPI/payment link integration.' },
  { icon: <IconStar />, title: 'Earns Points', desc: 'Loyalty points added automatically.' },
  { icon: <IconTrendingUp />, title: 'Delivery Update', desc: 'Real-time tracking updates via WhatsApp.' },
  { icon: <IconHeart />, title: 'Leaves Review', desc: 'AI prompts for review at the right moment.' },
  { icon: <IconMail />, title: 'Re-engagement', desc: 'Smart campaigns bring them back.' },
  { icon: <IconRefresh />, title: 'Repeat!', desc: 'The cycle continues, revenue grows.' },
];

const LifecycleConveyor = memo(({ dark }: { dark: boolean }) => {
  const { ref, inView } = useInView();
  const [expanded, setExpanded] = useState<number | null>(null);

  return (
    <section ref={ref} className={cls('py-20 overflow-hidden', dark ? 'bg-gray-900' : 'bg-gray-50')}>
      <div className="max-w-7xl mx-auto px-4">
        <h2 className={cls('text-3xl sm:text-4xl font-bold text-center mb-12', dark ? 'text-white' : 'text-gray-900', inView && 'animate-[fadeInUp_0.6s_ease]')}>From First Touch to Repeat Purchase</h2>
      </div>
      <div className="relative group">
        <div className="flex gap-6 px-8" style={{ animation: 'conveyor 40s linear infinite', animationPlayState: 'running', width: 'max-content' }} onMouseEnter={e => (e.currentTarget.style.animationPlayState = 'paused')} onMouseLeave={e => (e.currentTarget.style.animationPlayState = 'running')}>
          {[...LIFECYCLE_STEPS, ...LIFECYCLE_STEPS].map((s, i) => (
            <div
              key={i}
              onClick={() => setExpanded(expanded === i ? null : i)}
              className={cls(
                'flex-shrink-0 w-44 rounded-2xl p-5 cursor-pointer transition-all duration-300 border backdrop-blur-sm',
                dark ? 'bg-gray-800/80 border-gray-700 hover:border-violet-500' : 'bg-white/80 border-gray-200 hover:border-violet-500',
                expanded === i && 'ring-2 ring-violet-500'
              )}
            >
              <div className="text-violet-400 mb-3">{s.icon}</div>
              <div className={cls('text-sm font-semibold mb-1', dark ? 'text-white' : 'text-gray-900')}>{s.title}</div>
              {expanded === i && <p className={cls('text-xs mt-2', dark ? 'text-gray-400' : 'text-gray-600')} style={{ animation: 'fadeIn 0.3s ease' }}>{s.desc}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
});

/* ================================================================== */
/*  10. ANIMATED COUNTERS                                              */
/* ================================================================== */
const COUNTERS = [
  { prefix: '\u20B9', value: 2.4, suffix: ' Cr+', label: 'revenue tracked' },
  { prefix: '', value: 50000, suffix: '+', label: 'messages processed', decimals: 0 },
  { prefix: '', value: 1200, suffix: '+', label: 'tickets auto-resolved', decimals: 0 },
  { prefix: '', value: 98.5, suffix: '%', label: 'uptime' },
  { prefix: '', value: 15, suffix: '+', label: 'features unified', decimals: 0 },
  { prefix: '', value: 10, suffix: ' min', label: 'onboarding', decimals: 0 },
];

const FlipCounter = ({ target, prefix, suffix, decimals = 1, animate }: { target: number; prefix: string; suffix: string; decimals?: number; animate: boolean }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!animate) return;
    let start = 0;
    const duration = 2000;
    const step = (ts: number) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setVal(eased * target);
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [animate, target]);

  const display = decimals === 0 ? Math.round(val).toLocaleString() : val.toFixed(decimals);
  return (
    <div className="text-center">
      <div className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-2 font-mono" style={animate ? { animation: 'countUp 0.5s ease' } : {}}>
        {prefix}{display}{suffix}
      </div>
    </div>
  );
};

const AnimatedCounters = memo(({ dark: _dark }: { dark: boolean }) => {
  const { ref, inView } = useInView(0.2);
  return (
    <section ref={ref} className="py-20 px-4 bg-gray-950">
      <div className="max-w-6xl mx-auto grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-8">
        {COUNTERS.map((c, i) => (
          <div key={i} className="text-center" style={inView ? { animation: `fadeInUp 0.5s ease ${i * 0.1}s both` } : { opacity: 0 }}>
            <FlipCounter target={c.value} prefix={c.prefix} suffix={c.suffix} decimals={c.decimals ?? 1} animate={inView} />
            <div className="text-gray-500 text-sm mt-1">{c.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
});

/* ================================================================== */
/*  11. PRICING                                                        */
/* ================================================================== */
const PLANS = [
  {
    name: 'Starter', price: '\u20B94,999', period: '/month', popular: false,
    features: ['1 brand, 1 user', '1K contacts', 'AI Copilot + Concierge', 'Web widget', '5GB storage', 'Basic analytics', 'Email support'],
  },
  {
    name: 'Professional', price: '\u20B914,999', period: '/month', popular: true,
    features: ['3 brands, 5 users', '10K contacts', '1000 WhatsApp msgs', 'Advanced AI + custom training', '25GB storage', 'Journey tracking', 'Inventory management', 'Support automation', 'Email + Livechat'],
  },
  {
    name: 'Business', price: '\u20B934,999', period: '/month', popular: false,
    features: ['10 brands, 20 users', '50K contacts', '3000 WhatsApp msgs', 'Proposal automation', 'Lead management + CRM', 'Workflow automation', '80GB storage', 'White-label WhatsApp', 'Custom AI training', 'Dedicated account manager'],
  },
  {
    name: 'Enterprise', price: 'Custom', period: '', popular: false,
    features: ['Unlimited everything', 'Custom AI deployment', 'On-premise option', 'SOC 2 / HIPAA compliance', 'Custom integrations', 'Dedicated infrastructure', '99.9% SLA', '24/7 priority support', 'Strategic consulting'],
  },
];

const USAGE_RATES = [
  'Emails: \u20B90.25 each',
  'WhatsApp: \u20B91.00 each',
  'AI queries: \u20B90.013 each',
  'Extra seats: \u20B9500/month',
  'Contacts: \u20B90.50/100',
  'API: \u20B91,000 setup + \u20B90.10/100 calls',
];

const PricingSection = memo(({ dark, onContactClick }: { dark: boolean; onContactClick?: (plan: string) => void }) => {
  const { ref, inView } = useInView(0.1);
  return (
    <section ref={ref} className={cls('py-20 px-4', dark ? 'bg-gray-900' : 'bg-gray-50')} id="pricing">
      <div className="max-w-7xl mx-auto">
        <h2 className={cls('text-3xl sm:text-4xl font-bold text-center mb-4', dark ? 'text-white' : 'text-gray-900')}>Simple, Transparent Pricing</h2>
        <p className={cls('text-center mb-12 max-w-xl mx-auto', dark ? 'text-gray-400' : 'text-gray-600')}>Start free. Scale as you grow. No surprises.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PLANS.map((plan, i) => (
            <div
              key={plan.name}
              className={cls(
                'rounded-2xl p-6 border transition-all relative flex flex-col',
                plan.popular ? 'border-violet-500 border-2 shadow-xl shadow-violet-500/10' : (dark ? 'border-gray-700' : 'border-gray-200'),
                dark ? 'bg-gray-800' : 'bg-white',
              )}
              style={inView ? { animation: `cardDeal 0.5s ease ${i * 0.12}s both` } : { opacity: 0 }}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-violet-600 text-white text-xs font-bold rounded-full">Most Popular</div>
              )}
              <h3 className={cls('text-lg font-bold mb-1', dark ? 'text-white' : 'text-gray-900')}>{plan.name}</h3>
              <div className="mb-4">
                <span className={cls('text-3xl font-extrabold', dark ? 'text-white' : 'text-gray-900')}>{plan.price}</span>
                <span className={cls('text-sm', dark ? 'text-gray-400' : 'text-gray-500')}>{plan.period}</span>
              </div>
              <ul className="space-y-2 mb-6 flex-grow">
                {plan.features.map(f => (
                  <li key={f} className={cls('text-sm flex items-start gap-1', dark ? 'text-gray-300' : 'text-gray-700')}>
                    <IconCheck /><span>{f}</span>
                  </li>
                ))}
              </ul>
              <button onClick={() => onContactClick?.(plan.name)} className={cls('block w-full text-center py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer mt-auto', plan.popular ? 'bg-violet-600 text-white hover:bg-violet-700 shadow-lg shadow-violet-600/25' : (dark ? 'border border-gray-600 text-gray-300 hover:border-violet-500 hover:text-violet-400' : 'border border-gray-300 text-gray-700 hover:border-violet-500 hover:text-violet-600'))}>
                Contact Us
              </button>
              {plan.name !== 'Enterprise' ? (
                <div className={cls('mt-4 pt-4 border-t', dark ? 'border-gray-700' : 'border-gray-100')}>
                  <p className={cls('text-xs font-semibold mb-2', dark ? 'text-gray-400' : 'text-gray-500')}>Usage rates:</p>
                  <ul className="space-y-1">
                    {USAGE_RATES.map(r => <li key={r} className={cls('text-xs', dark ? 'text-gray-500' : 'text-gray-400')}>{r}</li>)}
                  </ul>
                </div>
              ) : (
                <div className={cls('mt-4 pt-4 border-t', dark ? 'border-gray-700' : 'border-gray-100')}>
                  <p className={cls('text-xs', dark ? 'text-gray-500' : 'text-gray-400')}>Custom pricing based on your requirements. All usage included.</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
});

/* ================================================================== */
/*  12. CTA SECTION                                                    */
/* ================================================================== */
const CtaSection = memo(() => {
  const { ref, inView } = useInView();
  const [email, setEmail] = useState('');
  const [spots, setSpots] = useState(23);

  useEffect(() => {
    const iv = setInterval(() => {
      setSpots(s => Math.max(3, s - (Math.random() > 0.7 ? 1 : 0)));
    }, 8000);
    return () => clearInterval(iv);
  }, []);

  return (
    <section ref={ref} className="py-20 px-4 bg-gradient-to-r from-violet-600 to-blue-600 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
      <div className="relative max-w-2xl mx-auto text-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={inView ? { animation: 'fadeInUp 0.6s ease' } : {}}>Ready to transform your retail business?</h2>
        <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto mt-8">
          <input
            type="email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="Enter your email"
            className="flex-1 px-5 py-3.5 rounded-full text-sm bg-white/20 border border-white/30 text-white placeholder-white/60 focus:outline-none focus:bg-white/30 backdrop-blur-sm"
          />
          <a href={SIGNUP} className="px-6 py-3.5 bg-white text-violet-700 font-bold rounded-full text-sm hover:bg-gray-100 transition-colors shadow-lg whitespace-nowrap">
            Get Started Free
          </a>
        </div>
        <p className="text-white/70 text-sm mt-4">No credit card required. 14-day trial.</p>
        <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full backdrop-blur-sm">
          <span className="w-2 h-2 bg-green-400 rounded-full" style={{ animation: 'spotsFlash 2s ease infinite' }} />
          <span className="text-white text-sm font-medium">First 100 brands get 3 months free — <span className="font-bold">{spots} spots remaining</span></span>
        </div>
      </div>
    </section>
  );
});

/* ================================================================== */
/*  13. FOOTER                                                         */
/* ================================================================== */
const Footer = memo(({ dark: _dark }: { dark: boolean }) => (
  <footer className="bg-gray-900 text-gray-400 py-16 px-4" id="about">
    <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-10">
      <div>
        <h3 className="text-white font-bold text-lg mb-4">AI Commerce OS</h3>
        <p className="text-sm mb-4">The unified AI-powered platform for modern D2C brands.</p>
        <div className="flex gap-3">
          <a href="#" className="text-gray-500 hover:text-white transition-colors"><IconTwitter /></a>
          <a href="#" className="text-gray-500 hover:text-white transition-colors"><IconLinkedIn /></a>
          <a href="#" className="text-gray-500 hover:text-white transition-colors"><IconGitHub /></a>
        </div>
      </div>
      <div>
        <h4 className="text-white font-semibold text-sm mb-4 uppercase tracking-wider">Product</h4>
        <ul className="space-y-2 text-sm">
          {[
            ['Shopper Concierge', '/solutions/shopper-concierge'],
            ['Workflow Automation', '/solutions/workflow-automation'],
            ['Analytics', '/solutions/analytics'],
            ['Integrations', '/solutions/integrations'],
            ['AI Copilot', '/brand-copilot-ai'],
          ].map(([l, h]) => (
            <li key={l}><a href={h} className="hover:text-white transition-colors">{l}</a></li>
          ))}
        </ul>
      </div>
      <div>
        <h4 className="text-white font-semibold text-sm mb-4 uppercase tracking-wider">Company</h4>
        <ul className="space-y-2 text-sm">
          {[
            ['About Us', '/about'],
            ['Success Stories', '/case-studies'],
            ['Careers', '/careers'],
          ].map(([l, h]) => (
            <li key={l}><a href={h} className="hover:text-white transition-colors">{l}</a></li>
          ))}
        </ul>
      </div>
      <div>
        <h4 className="text-white font-semibold text-sm mb-4 uppercase tracking-wider">Support & Legal</h4>
        <ul className="space-y-2 text-sm">
          {[
            ['FAQ', '/faq'],
            ['Privacy Policy', '/privacy'],
            ['Terms of Service', '/terms'],
          ].map(([l, h]) => (
            <li key={l}><a href={h} className="hover:text-white transition-colors">{l}</a></li>
          ))}
          <li><button onClick={() => { const el = document.getElementById('cta'); el?.scrollIntoView({ behavior: 'smooth' }); }} className="hover:text-white transition-colors text-left">Contact Us</button></li>
        </ul>
      </div>
    </div>
    <div className="max-w-7xl mx-auto mt-12 pt-8 border-t border-gray-800 text-center text-sm text-gray-500">
      &copy; 2026 AI Commerce OS by Idliappam. All rights reserved.
    </div>
  </footer>
));

/* ================================================================== */
/*  EXIT INTENT BANNER                                                 */
/* ================================================================== */
const ExitIntentBanner = memo(({ dark }: { dark: boolean }) => {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [msg, setMsg] = useState('');
  const [reply, setReply] = useState('');

  useEffect(() => {
    if (dismissed) return;
    const handler = (e: MouseEvent) => {
      if (e.clientY < 10 && !dismissed) setShow(true);
    };
    document.addEventListener('mousemove', handler);
    return () => document.removeEventListener('mousemove', handler);
  }, [dismissed]);

  const tryAI = () => {
    if (!msg.trim()) return;
    setReply('Great question! Our AI handles queries like this instantly. Sign up to see it in action with your own data!');
  };

  if (!show) return null;
  return (
    <div className="fixed top-0 left-0 w-full z-50" style={{ animation: 'slideDown 0.4s ease' }}>
      <div className={cls('p-4 shadow-2xl border-b', dark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200')}>
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center gap-3">
          <span className={cls('text-sm font-semibold whitespace-nowrap', dark ? 'text-white' : 'text-gray-900')}>Wait! Try our AI before you go</span>
          <div className="flex-1 flex gap-2 w-full sm:w-auto">
            <input type="text" value={msg} onChange={e => setMsg(e.target.value)} onKeyDown={e => e.key === 'Enter' && tryAI()} placeholder="Ask anything..." className={cls('flex-1 px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-violet-500', dark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400')} />
            <button onClick={tryAI} className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 transition-colors">Ask</button>
          </div>
          <button onClick={() => { setShow(false); setDismissed(true); }} className={cls('text-sm', dark ? 'text-gray-500 hover:text-white' : 'text-gray-400 hover:text-gray-900')}>
            <IconX />
          </button>
        </div>
        {reply && <p className={cls('max-w-3xl mx-auto mt-2 text-sm', dark ? 'text-gray-300' : 'text-gray-700')}>{reply}</p>}
      </div>
    </div>
  );
});

/* ================================================================== */
/*  KONAMI EASTER EGG                                                  */
/* ================================================================== */
const KonamiConfetti = memo(() => {
  const [active, setActive] = useState(false);
  const bufRef = useRef('');

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      bufRef.current += e.key.toLowerCase();
      if (bufRef.current.length > 10) bufRef.current = bufRef.current.slice(-10);
      if (bufRef.current.includes('konami')) {
        setActive(true);
        bufRef.current = '';
        setTimeout(() => setActive(false), 3000);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  if (!active) return null;
  const colors = ['#7c3aed', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444'];
  return (
    <div className="fixed inset-0 pointer-events-none z-50">
      {Array.from({ length: 60 }).map((_, i) => (
        <div
          key={i}
          className="absolute w-3 h-3 rounded-sm"
          style={{
            left: `${Math.random() * 100}%`,
            top: '-10px',
            backgroundColor: colors[i % colors.length],
            animation: `confettiFall ${1.5 + Math.random() * 2}s ease-in ${Math.random() * 0.5}s forwards`,
            transform: `rotate(${Math.random() * 360}deg)`,
          }}
        />
      ))}
    </div>
  );
});

/* ================================================================== */
/*  MAIN LANDING PAGE                                                  */
/* ================================================================== */
const API_BASE = process.env.REACT_APP_API_URL || 'https://web-production-3fcca.up.railway.app';

const LandingPage: React.FC = () => {
  const [dark, setDark] = useState(true);
  const [wipeActive, setWipeActive] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', company: '', message: '' });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);

  const handleContactSubmit = async () => {
    if (!contactForm.name || !contactForm.email) return;
    setContactSubmitting(true);
    try {
      await fetch(`${API_BASE}/api/v1/leads?brand_id=brand_18ae0e4b8f22d334`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: contactForm.name,
          email: contactForm.email,
          contactNumber: contactForm.phone,
          companyName: contactForm.company,
          source: 'website',
          interestLevel: 'high',
          stage: 'New Lead',
          status: 'hot',
          expectedDealValue: selectedPlan === 'Starter' ? 4999 : selectedPlan === 'Professional' ? 14999 : selectedPlan === 'Business' ? 34999 : 50000,
          notes: `Plan: ${selectedPlan}. Message: ${contactForm.message}`,
        }),
      });
      setContactSubmitted(true);
    } catch (e) {
      console.error('Contact form error:', e);
      setContactSubmitted(true); // Show success anyway — we don't want to scare them
    } finally {
      setContactSubmitting(false);
    }
  };

  const toggleDark = useCallback(() => {
    setWipeActive(true);
    setTimeout(() => {
      setDark(d => !d);
      setTimeout(() => setWipeActive(false), 500);
    }, 50);
  }, []);

  return (
    <div className={cls('min-h-screen transition-colors duration-300', dark ? 'bg-gray-950 text-white' : 'bg-white text-gray-900')}>
      <SEO
        title="Idliapam — AI-Powered SaaS Platform for Commerce & Developer Tools"
        description="Bug reporting (Glitch), in-app announcements (Radar), WhatsApp commerce API (Chutney), and AI-powered D2C dashboard. Built for SaaS teams and D2C brands."
        path="/"
        keywords="SaaS platform, bug reporting tool, in-app announcements, WhatsApp API, D2C commerce, AI commerce, Glitch bug tracker, Radar announcements, Chutney WhatsApp, idliapam"
        jsonLd={[
          organizationSchema,
          softwareSchema({ name: 'Idliapam', description: 'AI-powered SaaS platform with bug reporting, in-app announcements, WhatsApp commerce API, and D2C dashboard.', url: 'https://idliapam.in', category: 'BusinessApplication', features: ['Bug Reporting', 'In-App Announcements', 'WhatsApp Commerce API', 'AI Copilot', 'CRM Dashboard'] }),
        ]}
      />
      <style>{KEYFRAMES}</style>

      {/* Circle wipe overlay */}
      {wipeActive && (
        <div className="fixed inset-0 z-[60] pointer-events-none" style={{ backgroundColor: dark ? '#fff' : '#09090b', animation: 'circleWipe 0.5s ease forwards' }} />
      )}

      {/* Konami easter egg */}
      <KonamiConfetti />

      {/* Exit intent */}
      <ExitIntentBanner dark={dark} />

      {/* 1. Scroll progress */}
      <ScrollProgressBar />

      {/* 2. Navbar */}
      <Navbar dark={dark} toggleDark={toggleDark} />

      {/* 3. Hero */}
      <HeroSection dark={dark} />

      {/* 4. Ticker */}
      <LiveTicker />

      {/* 5. AI vs Human */}
      <AIvsHumanRace dark={dark} />

      {/* 6. Build Store Challenge */}
      <BuildStoreChallenge dark={dark} />

      {/* 7. Interactive Simulator */}
      <SimulatorTabs dark={dark} />

      {/* 8. Savings Calculator */}
      <SavingsCalculator dark={dark} />

      {/* 9. Lifecycle Conveyor */}
      <LifecycleConveyor dark={dark} />

      {/* 10. Animated Counters */}
      <AnimatedCounters dark={dark} />

      {/* 11. Pricing */}
      <PricingSection dark={dark} onContactClick={(plan) => { setSelectedPlan(plan); setShowContactForm(true); setContactSubmitted(false); setContactForm({ name: '', email: '', phone: '', company: '', message: '' }); }} />

      {/* 12. CTA */}
      <CtaSection />

      {/* 13. Footer */}
      <Footer dark={dark} />

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className={cls('w-full max-w-md rounded-2xl p-6 shadow-2xl', dark ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200')}>
            {contactSubmitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                <h3 className={cls('text-xl font-bold mb-2', dark ? 'text-white' : 'text-gray-900')}>Thank you!</h3>
                <p className={cls('text-sm mb-1', dark ? 'text-gray-400' : 'text-gray-500')}>We've received your request for the <strong className="text-violet-500">{selectedPlan}</strong> plan.</p>
                <p className={cls('text-sm', dark ? 'text-gray-400' : 'text-gray-500')}>Our team will reach out within 24 hours.</p>
                <button onClick={() => setShowContactForm(false)} className="mt-6 px-6 py-2 bg-violet-600 text-white text-sm font-semibold rounded-full hover:bg-violet-700">Close</button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className={cls('text-lg font-bold', dark ? 'text-white' : 'text-gray-900')}>Get Started with {selectedPlan}</h3>
                    <p className={cls('text-xs mt-0.5', dark ? 'text-gray-400' : 'text-gray-500')}>Fill in your details and we'll set up your account</p>
                  </div>
                  <button onClick={() => setShowContactForm(false)} className={cls('p-1 rounded-lg', dark ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100')}>
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className={cls('block text-xs font-medium mb-1', dark ? 'text-gray-400' : 'text-gray-500')}>Name *</label>
                    <input type="text" required value={contactForm.name} onChange={e => setContactForm({...contactForm, name: e.target.value})} placeholder="Your full name" className={cls('w-full px-3 py-2.5 text-sm rounded-lg outline-none transition-all', dark ? 'bg-gray-800 border border-gray-700 text-white focus:border-violet-500' : 'bg-gray-50 border border-gray-200 text-gray-900 focus:border-violet-500 focus:ring-2 focus:ring-violet-200')} />
                  </div>
                  <div>
                    <label className={cls('block text-xs font-medium mb-1', dark ? 'text-gray-400' : 'text-gray-500')}>Email *</label>
                    <input type="email" required value={contactForm.email} onChange={e => setContactForm({...contactForm, email: e.target.value})} placeholder="you@company.com" className={cls('w-full px-3 py-2.5 text-sm rounded-lg outline-none transition-all', dark ? 'bg-gray-800 border border-gray-700 text-white focus:border-violet-500' : 'bg-gray-50 border border-gray-200 text-gray-900 focus:border-violet-500 focus:ring-2 focus:ring-violet-200')} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={cls('block text-xs font-medium mb-1', dark ? 'text-gray-400' : 'text-gray-500')}>Phone</label>
                      <input type="tel" value={contactForm.phone} onChange={e => setContactForm({...contactForm, phone: e.target.value})} placeholder="+91 98765 43210" className={cls('w-full px-3 py-2.5 text-sm rounded-lg outline-none transition-all', dark ? 'bg-gray-800 border border-gray-700 text-white focus:border-violet-500' : 'bg-gray-50 border border-gray-200 text-gray-900 focus:border-violet-500 focus:ring-2 focus:ring-violet-200')} />
                    </div>
                    <div>
                      <label className={cls('block text-xs font-medium mb-1', dark ? 'text-gray-400' : 'text-gray-500')}>Company</label>
                      <input type="text" value={contactForm.company} onChange={e => setContactForm({...contactForm, company: e.target.value})} placeholder="Brand name" className={cls('w-full px-3 py-2.5 text-sm rounded-lg outline-none transition-all', dark ? 'bg-gray-800 border border-gray-700 text-white focus:border-violet-500' : 'bg-gray-50 border border-gray-200 text-gray-900 focus:border-violet-500 focus:ring-2 focus:ring-violet-200')} />
                    </div>
                  </div>
                  <div>
                    <label className={cls('block text-xs font-medium mb-1', dark ? 'text-gray-400' : 'text-gray-500')}>Message (optional)</label>
                    <textarea rows={2} value={contactForm.message} onChange={e => setContactForm({...contactForm, message: e.target.value})} placeholder="Tell us about your business..." className={cls('w-full px-3 py-2.5 text-sm rounded-lg outline-none transition-all resize-none', dark ? 'bg-gray-800 border border-gray-700 text-white focus:border-violet-500' : 'bg-gray-50 border border-gray-200 text-gray-900 focus:border-violet-500 focus:ring-2 focus:ring-violet-200')} />
                  </div>
                </div>
                <button onClick={handleContactSubmit} disabled={contactSubmitting || !contactForm.name || !contactForm.email} className="w-full mt-5 py-2.5 bg-violet-600 text-white text-sm font-semibold rounded-full hover:bg-violet-700 disabled:opacity-50 transition-all">
                  {contactSubmitting ? 'Submitting...' : 'Get in Touch'}
                </button>
                <p className={cls('text-center text-[11px] mt-3', dark ? 'text-gray-500' : 'text-gray-400')}>We'll respond within 24 hours. No spam, ever.</p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;